/**
 * Card Validation Utilities
 * Provides card number validation using Luhn algorithm and format checking
 */

/**
 * Card type patterns
 */
const CARD_PATTERNS = {
  visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
  mastercard: /^5[1-5][0-9]{14}$|^2(?:2(?:2[1-9]|[3-9][0-9])|[3-6][0-9][0-9]|7(?:[01][0-9]|20))[0-9]{12}$/,
  amex: /^3[47][0-9]{13}$/,
  discover: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
  dinersclub: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
  jcb: /^(?:2131|1800|35\d{3})\d{11}$/
};

/**
 * Luhn algorithm to validate card numbers
 * @param {string} cardNumber - Card number to validate
 * @returns {boolean} - Whether card number is valid
 */
export const luhnCheck = (cardNumber) => {
  // Remove spaces and non-digit characters
  const digits = cardNumber.replace(/\D/g, '');
  
  if (digits.length < 13 || digits.length > 19) {
    return false;
  }

  let sum = 0;
  let isEven = false;

  // Loop through values starting from the rightmost digit
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

/**
 * Detect card type from card number
 * @param {string} cardNumber - Card number
 * @returns {string|null} - Card type or null if unknown
 */
export const detectCardType = (cardNumber) => {
  const digits = cardNumber.replace(/\D/g, '');

  for (const [type, pattern] of Object.entries(CARD_PATTERNS)) {
    if (pattern.test(digits)) {
      return type;
    }
  }

  return null;
};

/**
 * Format card number with spaces
 * @param {string} cardNumber - Card number
 * @returns {string} - Formatted card number
 */
export const formatCardNumber = (cardNumber) => {
  const digits = cardNumber.replace(/\D/g, '');
  const cardType = detectCardType(digits);

  // American Express uses 4-6-5 format
  if (cardType === 'amex') {
    return digits.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3').trim();
  }

  // Most cards use 4-4-4-4 format
  return digits.replace(/(\d{4})/g, '$1 ').trim();
};

/**
 * Validate card number
 * @param {string} cardNumber - Card number to validate
 * @returns {CardValidationResult} - Validation result
 */
export const validateCardNumber = (cardNumber) => {
  const digits = cardNumber.replace(/\D/g, '');

  if (!digits) {
    return { isValid: false, error: 'Card number is required' };
  }

  if (digits.length < 13) {
    return { isValid: false, error: 'Card number is too short' };
  }

  if (digits.length > 19) {
    return { isValid: false, error: 'Card number is too long' };
  }

  if (!luhnCheck(digits)) {
    return { isValid: false, error: 'Invalid card number' };
  }

  const cardType = detectCardType(digits);
  if (!cardType) {
    return { isValid: false, error: 'Unknown card type' };
  }

  return { isValid: true, cardType };
};

/**
 * Validate expiry date
 * @param {string} month - Month (MM)
 * @param {string} year - Year (YY or YYYY)
 * @returns {CardValidationResult} - Validation result
 */
export const validateExpiryDate = (month, year) => {
  if (!month || !year) {
    return { isValid: false, error: 'Expiry date is required' };
  }

  const monthNum = parseInt(month, 10);
  if (monthNum < 1 || monthNum > 12) {
    return { isValid: false, error: 'Invalid month' };
  }

  // Convert 2-digit year to 4-digit
  let yearNum = parseInt(year, 10);
  if (year.length === 2) {
    yearNum += 2000;
  }

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (yearNum < currentYear) {
    return { isValid: false, error: 'Card has expired' };
  }

  if (yearNum === currentYear && monthNum < currentMonth) {
    return { isValid: false, error: 'Card has expired' };
  }

  if (yearNum > currentYear + 20) {
    return { isValid: false, error: 'Invalid expiry year' };
  }

  return { isValid: true };
};

/**
 * Validate CVV/Security Code
 * @param {string} cvv - Security code
 * @param {string} cardType - Card type (optional, for length validation)
 * @returns {CardValidationResult} - Validation result
 */
export const validateCVV = (cvv, cardType = null) => {
  if (!cvv) {
    return { isValid: false, error: 'Security code is required' };
  }

  const digits = cvv.replace(/\D/g, '');

  if (cardType === 'amex') {
    if (digits.length !== 4) {
      return { isValid: false, error: 'American Express requires 4-digit security code' };
    }
  } else {
    if (digits.length !== 3) {
      return { isValid: false, error: 'Security code must be 3 digits' };
    }
  }

  return { isValid: true };
};

/**
 * Validate cardholder name
 * @param {string} name - Cardholder name
 * @returns {CardValidationResult} - Validation result
 */
export const validateCardholderName = (name) => {
  if (!name || !name.trim()) {
    return { isValid: false, error: 'Cardholder name is required' };
  }

  if (name.trim().length < 2) {
    return { isValid: false, error: 'Name is too short' };
  }

  if (!/^[a-zA-Z\s'-]+$/.test(name)) {
    return { isValid: false, error: 'Name contains invalid characters' };
  }

  return { isValid: true };
};

/**
 * Validate all card details
 * @param {CardDetails} cardDetails - Complete card details
 * @returns {Object} - Validation result with errors for each field
 */
export const validateCardDetails = (cardDetails) => {
  const errors = {};

  const nameValidation = validateCardholderName(cardDetails.cardholderName);
  if (!nameValidation.isValid) {
    errors.cardholderName = nameValidation.error;
  }

  const cardValidation = validateCardNumber(cardDetails.cardNumber);
  if (!cardValidation.isValid) {
    errors.cardNumber = cardValidation.error;
  }

  const expiryValidation = validateExpiryDate(cardDetails.expiryMonth, cardDetails.expiryYear);
  if (!expiryValidation.isValid) {
    errors.expiry = expiryValidation.error;
  }

  const cvvValidation = validateCVV(cardDetails.cvv, cardValidation.cardType);
  if (!cvvValidation.isValid) {
    errors.cvv = cvvValidation.error;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    cardType: cardValidation.cardType
  };
};

/**
 * Mask card number for display (show only last 4 digits)
 * @param {string} cardNumber - Card number
 * @returns {string} - Masked card number
 */
export const maskCardNumber = (cardNumber) => {
  const digits = cardNumber.replace(/\D/g, '');
  if (digits.length < 4) return '****';
  return '**** **** **** ' + digits.slice(-4);
};

export default {
  luhnCheck,
  detectCardType,
  formatCardNumber,
  validateCardNumber,
  validateExpiryDate,
  validateCVV,
  validateCardholderName,
  validateCardDetails,
  maskCardNumber
};
